import { ChiptaElement, html, css } from 'chipta-libs/main.js';
import { url, getQueryParam } from 'chipta-libs/utils/url.js';
import ticketshopAPI from 'chipta-libs/api/ticketshop.js';
import { accountAPI } from 'chipta-libs/api/account.js';
import { layoutStyles } from 'chipta-libs/styles/layout.js';
import { formStyles } from 'chipta-libs/styles/form.js';
import './styled-button.js';
import './styled-input.js';


class LoginForm extends layoutStyles(formStyles(ChiptaElement)) {

  static get properties() {
    return {
      email: {type: String},
      loginPromise: {type: Promise},
      loginLoading: {type: Boolean},
      loginErrorCode: {type: String},
      loginErrorMessage: {type: String},
      signupEmailResending: {type: Boolean},
      signupEmailResent: {type: Boolean}
    };
  }

  constructor() {
    super();
    this.email = '';
  }

  render() {

    return html`

      <section class="content-box">

        <div id="links">
          <a class="link" href=${url('account/sign-up/')}>
            ${gettext('signup')}
          </a> /
          <a class="link" href=${this.forgotPasswordUrl} id="forgot-password-link">
            ${gettext('forgot_password')}
          </a>
        </div>

        ${this.loginErrorMessageEl}

        <label class="field">
          <div class="label">${gettext('mail')}</div>
          <div class="input">
            <styled-input>
              <input
                type="email"
                id="email"
                placeholder=${gettext('mail')}
                pattern=${jsVar('emailRegex', '')}
                value=${this.email}
                @change=${this.handleEmailChange}
                @input=${this.handleEmailInput}
                form="login-form"
                required
                autofocus
              ></input>
            </styled-input>
          </div>
        </label>

        <label class="field">

          <div class="label">${gettext('password')}</div>
          <div class="input">

            <styled-input>
              <input
                type="password"
                id="password"
                placeholder=${gettext('password')}
                form="login-form"
                required
              >
            </styled-input>

          </div>
        </label>

        <div id="bottom">

          <label class="remember-me">
            <input type="checkbox" id="rememberMe" form="login-form"> ${gettext('remember_me')}
          </label>

          <div class="login-button">
            <styled-button method="action" ?saving=${this.loginLoading}>
              <button type="submit" form="login-form">${gettext('log_in')}</button>
            </styled-button>
          </div>

        </div>

        <form id="login-form" @submit=${this.handleLoginFormSubmit}></form>

      </section>

    `;

  }

  get loginErrorMessageEl() {

    if (!this.loginErrorCode) {
      return;
    }

    return html`
      <div class="error">
        ${this.loginErrorMessage}
      </div>
      ${this.resendSignupEmailEl}
    `;

  }

  get resendSignupEmailEl() {

    if (this.loginErrorCode !== 'complete_signup') {
      return;
    }

    return html`
      <styled-button
        method="action"
        size="small"
        ?saving=${this.signupEmailResending}
        ?saved=${this.signupEmailResent}
        style="margin-bottom: 20px;"
      >
        <button @click=${this.handleResendSignupEmailButtonClick}>
          ${this.resendSignupEmailButtonText}
        </button>
      </styled-button>
    `;

  }

  get resendSignupEmailButtonText() {
    if (this.signupEmailResent) {
      return gettext('signup_email_resent');
    } else {
      return gettext('resend_signup_email');
    }
  }

  handleResendSignupEmailButtonClick() {

    this.signupEmailResending = true;

    accountAPI.requestSignUp(this.email).then(() => {
      this.signupEmailResent = true;
    }).catch(() => {
      this.signupEmailResent = false;
    }).finally(() => {
      this.signupEmailResending = false;
    });

  }

  handleEmailChange(event) {
    this.email = event.target.value;
  }

  handleEmailInput(event) {
    this.email = event.target.value;
    // Force re-render to update the forgot password link
    this.requestUpdate();
  }

  handleLoginFormSubmit(event) {

    event.preventDefault();

    this.loginLoading = true;
    this.loginErrorCode = null;
    this.loginErrorMessage = null;

    const email = this.el('#email').value;
    const password = this.el('#password').value;
    const rememberMe = this.el('#rememberMe').checked;

    ticketshopAPI.login(email, password, rememberMe).then(() => {
      window.location.href = url(
        (getQueryParam('next') || jsVar('accountHomeUrl')),
        {'user-logged': '1'}
      );
    }).catch(error => {
      this.loginErrorCode = error.code;
      this.loginErrorMessage = error.message;
    }).finally(() => {
      this.loginLoading = false;
    });

  }

  get forgotPasswordUrl() {
    const baseUrl = url('account/password-reset/');
    const email = this.email || '';

    if (email.trim()) {
      const urlObj = new URL(baseUrl, window.location.origin);
      urlObj.searchParams.set('email', email.trim());
      return urlObj.toString();
    }

    return baseUrl;
  }

  static get styles() {

    return css`

      :host {
        display: block;
        font-size: 15px;
      }

      * {
        box-sizing: border-box;
      }

      #links {
        display: flex;
        justify-content: end;
        font-size: 13px;
        padding: 5px 0;
      }

      .link {
        margin: 0 5px;
      }

      .error {
        margin-bottom: 15px;
        font-size: 13px;
        color: #900;
      }

      .field {
        display: block;
        margin-bottom: 10px;
      }

      .field .label {
        text-transform: uppercase;
        color: #444;
        font-size: 12px;
        font-weight: 600;
      }

      .field .input {
        margin-top: 5px;
      }

      .field .input styled-input,
      .field .input styled-input input {
        width: 100%;
        max-width: 450px;
      }

      #bottom {
        display: flex;
        justify-content: space-between;
        margin-top: 5px;
        width: 100%;
      }

      .remember-me {
        font-size: 13px;
      }

      .error[hidden] {
        display: none;
      }

      @media screen and (max-width: 500px) {

        .error {
          margin-top: 15px;
          margin-left: 0;
          font-size: unset;
        }

        .bottom {
          margin-top: 15px;
          margin-left: 0;
          width: 100%;
        }

      }

    `;

  }

}

customElements.define('login-form', LoginForm);
